import { BaseSyntheticEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import { Box } from '@material-ui/core';

import {
  ContainerType,
  DivContainerState,
  EmptyPlaceholder,
  Icon,
  ImageContainerState,
  UniversalContainerState,
  Wysiwyg,
} from '@components';
import {
  DataVisualizationListItemSchema,
  deployment,
  experience,
  global,
  ExperienceDeployment,
  InfoEmailTemplatePayload,
  ModuleDisclosureStation,
  ExperienceItemMindset,
} from '@modules';
import { useAppDispatch } from '@store';
import { useHistory, parse, textTemplate, network } from '@utils';
import { Section, TOP_BAR_CTX_VALUE, TopBarContext } from '@layouts';
import { variables } from '@styles';

import { LibrariesPanelWrapper } from './LibrariesPanel/LibrariesPanelWrapper';
import { ModuleLibrary } from './ModuleLibrary';
import {
  SampleTemplate,
  DeploymentSettings,
  Treatments,
  RightPanelLayout,
  ModuleSettings,
  EMPTY_MODULE_NOTE,
  DELETE_MODULE_ICON_ID,
} from './SampleTemplate';

import { ContentTargetingOverlay } from './Overlays';

import { HTMLElementWithDocument } from './SampleTemplate/SampleTemplate.types';
import { getUniqueStyles, positionSelectedModuleIframeOverlay } from './SampleTemplate/SampleTemplate.utils';

import {
  ChosenImage,
  DefaultChosenTreat,
  DeletedExperience,
  ElemInputActiveType,
  HTMLElementWithOriginalInnerHTML,
  HTMLImageElementWithOriginalInnerHTML,
  LeftPanelType,
  SingleModulesWrapper,
  SplitterData,
  TemplateViewProps,
  TreatmentBuilderContextValue,
  TreatmentPanelType,
} from './TemplateView.types';
import { useStyles } from './TemplateView.styles';
import {
  // DEFAULT_CHOSEN_DV,
  DEFAULT_CHOSEN_IMAGE,
  DEFAULT_DELETED_EXPERIENCE,
  DEFAULT_IMAGE_WRAPPER_HEIGHT,
  DEFAULT_IMAGE_WRAPPER_WIDTH,
  DEPLOYMENTS_MINDSET_DND_ID,
  LeftPanelTypes,
  Libraries,
  RightPanelElems,
  TreatmentBuilderContext,
} from './TemplateView.const';
import {
  convertButtonStylesToString,
  convertWysiwygStylesToString,
  convertFromCamelToHyphenCase,
  // getLibrariesWrapperTitle,
  getLibraryPanelComponent,
  getUniversalInitialValues,
  getRedefinedWysiwygStyles,
  transformStylesStringToObject,
  getSentenceCasedText,
  getDivInitialValues,
  getBackgroundImageStyles,
  getRedefinedButtonStyles,
  getButtonSettingsInitialValues,
  removePreviousConditionalCommentNode,
  createButtonConditionalCommentVML,
  prependConditionalComment,
  terminateStylesString,
  onImageReady,
} from './TemplateView.utils';
import { content } from '@content';
import { HTMLEditor } from '@components/Wysiwyg/HTMLEditor';
import { TemplateList } from '@routes/Deployments/TemplateList';
import { TDVItem } from '@components/Wysiwyg/DVPanel/types';
import { TreatmentCardProps } from './SampleTemplate/RightPanel/Treatments/TreatmentCard';
import {
  ButtonBackgroundImageState,
  ButtonSettingsState,
} from '@components/Wysiwyg/ButtonSettings/ButtonSettings.props';
import {
  resizeModalIframe,
  positionIframeOverlay,
} from '@routes/TemplateView/SampleTemplate/ModuleModal/ModuleModal.utils';
import { MODULE_INTERACTION_ACTIVE_ID } from './SampleTemplate/ModuleModal/ModuleModal.const';
import { SubjectLinePreheader } from './SubjectLinePreheader';

/**
 Viewing Deployment -
 TemplateView route component
 @returns {JSX.Element}
 */

export const TemplateView = ({
  templateId,
  templates,
  selectedTemplate,
  onTemplateSelect,
  onTemplateUpload,
}: TemplateViewProps): JSX.Element => {
  const styles = useStyles();
  const dispatch = useAppDispatch();

  const history = useHistory();
  const {
    enterprise,
    businessUnit: businessUnitId = 0,
    deployment: deploymentId,
    program,
    mailFile,
    template,
  } = history.query;

  const {
    status: templateLoadingStatus,
    data: { deploymentEmailTemplates },
  } = deployment.useInfoEmailTemplate();

  const [chosenElemId, setChosenElemId] = useState('');
  const [chosenModuleElemId, setChosenModuleElemId] = useState('');
  const [usedExperiences, setUsedExperiences] = useState<number[]>([]);
  const [showLibraryPanel, setShowLibraryPanel] = useState(false);
  const [chosenLibrary, setChosenLibrary] = useState<Libraries | null>(Libraries.IMAGES);
  const [rightPanelElem, setRightPanelElem] = useState<RightPanelElems>(RightPanelElems.DEPLOYMENT_SETTINGS);
  const [editableTreatmentId, setEditableTreatmentId] = useState<number | null>(null);
  const [showAddTreatmentPanel, setShowAddTreatmentPanel] = useState(false);
  const [showModuleEditor, setShowModuleEditor] = useState(false);
  const [deletedExperience, setDeletedExperience] = useState<DeletedExperience>(DEFAULT_DELETED_EXPERIENCE);
  const [chosenModuleInnerHtml, setChosenModuleInnerHtml] = useState('');
  const [moduleIndex, setModuleIndex] = useState<number>(0);
  const [defaultChosenTreats, setDefaultChosenTreats] = useState<DefaultChosenTreat[]>([]);
  const [chosenElemStyles, setChosenElemStyles] = useState('');
  const [wysiwygContainerType, setWysiwygContainerType] = useState<ContainerType>('text');
  const [chosenImage, setChosenImage] = useState<ChosenImage>(DEFAULT_CHOSEN_IMAGE);
  const [chosenDV, setChosenDV] = useState<DataVisualizationListItemSchema>();
  const [chosenExperienceId, setChosenExperienceId] = useState<number | null>(null);
  const [singleModulesWrapper, setSingleModulesWrapper] = useState<SingleModulesWrapper>(null);
  const [isTreatmentTouched, setIsTreatmentTouched] = useState(false);
  const [chosenModuleId, setChosenModuleId] = useState<number | null>(null);
  const [currentExperienceId, setCurrentExperienceId] = useState<number | null>(null);
  const [isModuleChanged, setIsModuleChanged] = useState(false);
  const [newExperienceCloned, setNewExperienceCloned] = useState<SplitterData>({
    cloned: false,
    splitterId: null,
    experienceId: null,
  });
  const [deploymentsInfo, setDeploymentsInfo] = useState<ExperienceDeployment[]>([]);
  const deploymentData = deployment.useDeploymentData();
  const isMailFileUploading = useMemo(() => deploymentData?.isMailFileUploading ?? false, [deploymentData]);
  const [disclosure, setDisclosure] = useState<ModuleDisclosureStation | null>(null);
  const [experiencesIds, setExperiencesIds] = useState<number[]>([]);
  const [treatmentPanelType, setTreatmentPanelType] = useState<TreatmentPanelType>('logicBuilder');
  const [isTargetContentShown, setIsTargetContentShown] = useState(false);
  const [moduleHtml, setModuleHtml] = useState<string>('');
  const [elemInputActive, setElemInputActive] = useState<ElemInputActiveType>('disabled');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [editMode, setEditMode] = useState('no-code');
  const [showRemoveExperienceModal, setShowRemoveExperienceModal] = useState(false);
  const [showRemoveTreatmentModal, setShowRemoveTreatmentModal] = useState(false);
  const [showRemoveWithRevisions, setShowRemoveWithRevisions] = useState(false);
  const [lastEmailTemplateId, setLastEmailTemplateId] = useState<number>();
  const [isEditorChanged, setIsEditorChanged] = useState(false);
  const [moduleDataVisualizationStation, setModuleDataVisualizationStation] = useState<any>({});
  const [DVItems, setDVItems] = useState<TDVItem[]>([]);
  const [showSelectTemplate, setShowSelectTemplate] = useState(false);
  const [leftPanelType, setLeftPanelType] = useState<LeftPanelType>(LeftPanelTypes.IMAGES);

  const [isModuleEditorOpen, setIsModuleEditorOpen] = useState(false);
  const [selectedActiveModule, setSelectedActiveModule] = useState<HTMLElement | null>(null);

  const [resetSubjectLinePreheader, setResetSubjectLinePreheader] = useState(false);

  const lastDeploymentIdRef = useRef<number | null>(null);

  // setup state for close/save buttons in right panel
  const [onModuleSave, setOnModuleSave] = useState<VoidFunction>(() => {});
  const [onModuleClose, setOnModuleClose] = useState<VoidFunction>(() => {});

  // Determine whether the select template block should appear
  useEffect(() => {
    const hasSelectedTemplate = deploymentEmailTemplates?.some((item) => item.isSelected);

    switch (templateLoadingStatus) {
      case 'loading':
        setShowSelectTemplate(false);
        break;
      case 'success':
        setShowSelectTemplate(!hasSelectedTemplate);
        break;
      case 'error':
        setShowSelectTemplate(false);
        break;
      default:
        setShowSelectTemplate(false);
        break;
    }
  }, [deploymentEmailTemplates, templateLoadingStatus]);

  const buttonSettingsInitialValues = useMemo(
    () => getButtonSettingsInitialValues(chosenModuleElemId, transformStylesStringToObject(chosenElemStyles)),
    // [isModuleChanged, chosenModuleElemId],
    [chosenModuleElemId, chosenElemStyles],
  );

  const universalInitialValues = useMemo(
    () => getUniversalInitialValues(chosenModuleElemId, transformStylesStringToObject(chosenElemStyles)),
    // [isModuleChanged, chosenModuleElemId],
    [chosenElemStyles, chosenModuleElemId],
  );

  const sidebarRef = useRef<HTMLDivElement>(null);
  const contextValues: TreatmentBuilderContextValue = {
    chosenElemId,
    chosenModuleElemId,
    chosenExperienceId,
    experiencesIds,
    editableTreatmentId,
    showAddTreatmentPanel,
    showModuleEditor,
    chosenModuleInnerHtml,
    moduleIndex,
    defaultChosenTreats,
    chosenElemStyles,
    wysiwygContainerType,
    chosenLibrary,
    singleModulesWrapper,
    isTreatmentTouched,
    chosenModuleId,
    currentExperienceId,
    newExperienceCloned,
    deploymentsInfo,
    isModuleChanged,
    disclosure,
    isTargetContentShown,
    elemInputActive,
    cursorPosition,
    editMode,
    showRemoveExperienceModal,
    showRemoveTreatmentModal,
    showRemoveWithRevisions,
    // lastEmailTemplateId,
    moduleDataVisualizationStation,
    DVItems,
    deleteModuleIconId: DELETE_MODULE_ICON_ID,
    leftPanelType,
    selectedActiveModule,
    onModuleClose,
    setOnModuleClose,
    onModuleSave,
    setOnModuleSave,
    positionSelectedModuleIframeOverlay,
    setDVItems,
    setModuleDataVisualizationStation,
    // setLastEmailTemplateId,
    setShowRemoveWithRevisions,
    setShowRemoveTreatmentModal,
    setShowRemoveExperienceModal,
    setEditMode,
    setCursorPosition,
    setDisclosure,
    setDeploymentsInfo,
    setExperiencesIds,
    setNewExperienceCloned,
    setCurrentExperienceId,
    setChosenModuleId,
    setIsTreatmentTouched,
    setSingleModulesWrapper,
    setChosenElemId,
    setChosenModuleElemId,
    setChosenExperienceId,
    setEditableTreatmentId,
    setShowAddTreatmentPanel,
    setShowModuleEditor,
    setChosenModuleInnerHtml,
    setModuleIndex,
    setDefaultChosenTreats,
    setChosenElemStyles,
    setWysiwygContainerType,
    setChosenLibrary,
    setIsModuleChanged,
    setIsTargetContentShown,
    setElemInputActive,
    setLeftPanelType,
  };
  const [topBarCtx, setTopBarCtx] = useContext(TopBarContext);

  const divInitialValues = useMemo(
    () => getDivInitialValues(transformStylesStringToObject(chosenElemStyles)),
    // dependencies should be [chosenElemStyles, chosenModuleElemId]
    [chosenElemStyles, chosenModuleElemId],
  );

  const handleDragEnd = useCallback(
    async (result: DropResult) => {
      const { reason, type, source, destination } = result;

      // reorder mindsets
      if (
        reason === 'DROP' &&
        type === DEPLOYMENTS_MINDSET_DND_ID &&
        source.droppableId === DEPLOYMENTS_MINDSET_DND_ID &&
        destination?.droppableId === DEPLOYMENTS_MINDSET_DND_ID
      ) {
        await dispatch(experience.actions.moveMindset({ from: source.index, to: destination.index }));

        setIsTreatmentTouched(true);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    // Only run if deploymentId is defined and has changed
    if (!deploymentId || !deploymentData?.id || lastDeploymentIdRef.current === deploymentId) return;

    lastDeploymentIdRef.current = deploymentId;

    (async () => {
      const response = await network.get<InfoEmailTemplatePayload>({
        key: 'deployment_info_email_template',
        params: { deployment: deploymentId },
      });

      const selectedTemplateId = response?.deploymentEmailTemplates?.find((item) => item.isSelected)?.id;

      if (selectedTemplateId && template !== selectedTemplateId) {
        history.push('deployments', {
          enterprise,
          businessUnit: businessUnitId,
          deployment: deploymentId,
          program,
          mailFile: deploymentData.mailFile?.id,
          template: selectedTemplateId,
        });

        setTopBarCtx((prev) => ({
          ...prev,
          isTemplateListVisible: false,
        }));
      }
    })();
    // Only depend on deploymentId and deploymentData?.id
    // }, [deploymentId, deploymentData?.id, template]);
  }, [
    deploymentId,
    deploymentData?.id,
    template,
    deploymentData?.mailFile?.id,
    history,
    enterprise,
    businessUnitId,
    program,
    setTopBarCtx,
  ]);

  //removing items mindsets on template view loading
  useEffect(() => {
    dispatch(experience.actions.clearItemsMindsets());
  }, [dispatch, templateId]);

  // reset the Subject Line and Preheader component view
  useEffect(() => {
    setResetSubjectLinePreheader(true);
  }, [templateId]);

  // function for dragging Experience to the Template
  const handleDragExperience = (e: BaseSyntheticEvent) => {
    const experienceId = parse.integer(e.currentTarget.getAttribute('data-id'));
    const nativeEvent = e.nativeEvent as DragEvent;
    nativeEvent.dataTransfer?.setData('application/json', JSON.stringify({ cursor: 0, experienceId }));
  };

  // function for dragging Single Module to the Template
  const handleModuleDrag = useCallback(
    (e: BaseSyntheticEvent) => {
      const moduleId = parse.integer(e.currentTarget.getAttribute('data-module-id'));
      const nativeEvent = e.nativeEvent as DragEvent;
      nativeEvent.dataTransfer?.setData(
        'application/json',
        JSON.stringify({ cursor: 0, moduleId, chosenModuleId, experienceId: chosenExperienceId }),
      );
    },
    [chosenExperienceId, chosenModuleId],
  );

  // getting Template by the deployment ID
  useEffect(() => {
    const deploymentGetById = async () => {
      if (deploymentId) {
        await dispatch(deployment.thunk.getById(deploymentId));
      }
    };

    if (deploymentId) {
      deploymentGetById();
      // dispatch(deployment.thunk.getById(deploymentId));
    }
  }, [deploymentId, templateId, dispatch]);

  const handleSSEMailFileLoadingConnect = useCallback(() => {
    const SSEUrl = process.env.REACT_APP_SSE_MAIL_FILE_LOADING?.replace('%d', mailFile?.toString() ?? '') ?? '';

    const mailFileLoadingChannel = new EventSource(SSEUrl);

    mailFileLoadingChannel.onmessage = (e: any) => {
      try {
        const data = JSON.parse(e.data);

        if (data.status === 'done') {
          dispatch(deployment.actions.changeDataMailLoadingStatus(false));
        }

        if (data.message === 'error') {
          dispatch(
            global.actions.enqueueNotification({
              message: content.SomethingWentWrongTryAgain,
              options: { variant: 'error' },
            }),
          );
        }

        mailFileLoadingChannel.close();
      } catch (err) {
        console.log(err);
      }
    };
  }, [mailFile, dispatch]);

  useEffect(() => {
    if (isMailFileUploading && deploymentId && mailFile) {
      handleSSEMailFileLoadingConnect();
    }
  }, [mailFile, isMailFileUploading, deploymentId, handleSSEMailFileLoadingConnect]);

  const handleChooseLibrary = useCallback(
    (libraryName: Libraries | null) => {
      setChosenLibrary(libraryName);
    },
    [setChosenLibrary],
  );

  const handleUniversalStyleChange = useCallback(
    (state: UniversalContainerState) => {
      const iframe = document.getElementById('module-modal') as HTMLElementWithDocument;
      const changingElem = iframe.contentDocument.getElementById(
        chosenModuleElemId,
      ) as HTMLElementWithOriginalInnerHTML;

      setChosenLibrary(Libraries.DATA_ATTRIBUTES);

      const redefinedWysiwygStyles = getRedefinedWysiwygStyles(state);

      redefinedWysiwygStyles.backgroundColor = redefinedWysiwygStyles.backgroundColor.toLowerCase();
      redefinedWysiwygStyles.color = redefinedWysiwygStyles.color.toLowerCase();

      const hyphenCasedStylesString = convertFromCamelToHyphenCase(
        convertWysiwygStylesToString(redefinedWysiwygStyles),
      );
      const uniqueStyles = getUniqueStyles(
        // `${chosenElemStyles}; ${hyphenCasedStylesString} \nborder-color: ${variables.color.primary.mainPurple};`,
        `${terminateStylesString(chosenElemStyles)} ${terminateStylesString(hyphenCasedStylesString)}`,
      );

      if (changingElem) {
        changingElem.setAttribute('style', uniqueStyles);

        if (redefinedWysiwygStyles.textTransform === 'sentence') {
          changingElem.innerHTML = getSentenceCasedText(changingElem?.innerHTML.trim());
        }
        if (redefinedWysiwygStyles.textTransform === 'unset') {
          changingElem.innerHTML = changingElem.getAttribute('originalInnerHTML') || changingElem.innerHTML;
        }
      }

      resizeModalIframe(iframe as HTMLIFrameElement, false);
      positionSelectedModuleIframeOverlay(selectedActiveModule);

      // adjust the position of the active overlay
      const activeOverlay = iframe.contentDocument.getElementById(MODULE_INTERACTION_ACTIVE_ID);
      if (activeOverlay && changingElem) {
        positionIframeOverlay(activeOverlay, changingElem);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chosenModuleElemId],
  );

  const handleButtonStyleChange = useCallback(
    (state: ButtonSettingsState) => {
      const iframe = document.getElementById('module-modal') as HTMLElementWithDocument;
      const changingElem = iframe.contentDocument.getElementById(
        chosenModuleElemId,
      ) as HTMLElementWithOriginalInnerHTML;

      setChosenLibrary(Libraries.DATA_ATTRIBUTES);

      const redefinedButtonStyles = getRedefinedButtonStyles(state);

      // if the background image is not set, empty or set to 'none',
      // check for and remove the previous conditional comment code node
      removePreviousConditionalCommentNode(changingElem);

      const stylesArray = chosenElemStyles.split(';').map((item) => item.trim());

      redefinedButtonStyles.backgroundColor = redefinedButtonStyles.backgroundColor?.toLowerCase();
      redefinedButtonStyles.borderColor = redefinedButtonStyles.borderColor?.toLowerCase();
      redefinedButtonStyles.color = redefinedButtonStyles.color?.toLowerCase();

      const msoHideStyle = 'mso-hide:all;';
      let includeMsoHideStyle = false;

      if (redefinedButtonStyles) {
        const hyphenCasedStylesString = convertButtonStylesToString(redefinedButtonStyles);
        const uniqueStyles = getUniqueStyles(
          `${terminateStylesString(stylesArray.join(';'))} ${terminateStylesString(hyphenCasedStylesString)}`,
        );

        if (changingElem) {
          changingElem.setAttribute('style', uniqueStyles);

          if (redefinedButtonStyles.textTransform === 'sentence') {
            changingElem.innerHTML = getSentenceCasedText(changingElem?.innerHTML.trim());
          }
          if (redefinedButtonStyles.textTransform === 'unset') {
            changingElem.innerHTML = changingElem.getAttribute('originalInnerHTML') || changingElem.innerHTML;
          }
        }

        // update the conditional comment and VML if there is a background image
        const buttonBackground = redefinedButtonStyles.backgroundImage || '';

        if (buttonBackground && buttonBackground !== 'none' && buttonBackground.includes('url(')) {
          // create the VML for Outlook
          const vml = createButtonConditionalCommentVML(changingElem);

          if (vml) {
            // prepend the VML comment to the button element
            prependConditionalComment(changingElem, vml);

            includeMsoHideStyle = true;
          }
        }
      }

      if (includeMsoHideStyle) {
        // set the mso-hide style to the button element
        changingElem.setAttribute(
          'style',
          getUniqueStyles(`${terminateStylesString(changingElem.getAttribute('style'))} ${msoHideStyle}`),
        );
      } else {
        // remove the mso-hide style from the button element
        changingElem.setAttribute(
          'style',
          getUniqueStyles(changingElem.getAttribute('style')?.replace(/mso-hide:all;/g, '') || ''),
        );
      }

      resizeModalIframe(iframe as HTMLIFrameElement, false);
      positionSelectedModuleIframeOverlay(selectedActiveModule);

      // adjust the position of the active overlay
      const activeOverlay = iframe.contentDocument.getElementById(MODULE_INTERACTION_ACTIVE_ID);
      if (activeOverlay && changingElem) {
        positionIframeOverlay(activeOverlay, changingElem);
      }
    },
    // xeslint-disable-next-line react-hooks/exhaustive-deps
    // [chosenModuleElemId],
    [chosenElemStyles, chosenModuleElemId, selectedActiveModule],
  );

  const handleImageStyleChange = useCallback(
    (state: ImageContainerState) => {
      const iframe = document.getElementById('module-modal') as HTMLElementWithDocument;
      const imageElem = iframe.contentDocument.getElementById(
        chosenModuleElemId,
      ) as HTMLImageElementWithOriginalInnerHTML;

      if (chosenModuleElemId !== state?.chosenModuleElemId || !imageElem) {
        return;
      }

      const imageStyles = imageElem.getAttribute('style') || '';

      const filteredStyles = imageStyles
        .split(';')
        .map((item) => item.replace('\n', ''))
        .excludeStrs(['width:', 'height:'])
        .join(';\n');

      if (imageElem && ([Libraries.IMAGES, Libraries.DATA_VISUALIZATIONS].includes(chosenLibrary!) || !chosenLibrary)) {
        imageElem.setAttribute('src', state?.imageSrc || '');
        imageElem.setAttribute('style', filteredStyles);
      }

      if (imageElem) {
        imageElem.setAttribute('width', state?.width || '');
        imageElem.setAttribute('height', state?.height || '');
      }

      onImageReady(imageElem, () => {
        resizeModalIframe(iframe as HTMLIFrameElement, false);
        positionSelectedModuleIframeOverlay(selectedActiveModule);

        // adjust the position of the active overlay
        const activeOverlay = iframe.contentDocument.getElementById(MODULE_INTERACTION_ACTIVE_ID);
        if (activeOverlay && imageElem) {
          positionIframeOverlay(activeOverlay, imageElem);
        }
      });
    },
    [chosenLibrary, chosenModuleElemId, selectedActiveModule],
  );

  const handleLinkChange = useCallback(
    (href: string) => {
      const { contentDocument } = document.getElementById('module-modal') as HTMLElementWithDocument;
      const activeElement = contentDocument.getElementById(chosenModuleElemId) as HTMLElementWithOriginalInnerHTML;

      // logic for accessing the link URL based on the tag name
      const tagName = activeElement?.tagName.toLowerCase() ?? '';

      // if tagname is A, set the href and temphref attributes
      if (tagName === 'a') {
        activeElement?.setAttribute('href', href);
        activeElement?.setAttribute('temphref', href);

        // update the conditional comment if the data-element-type is button
        if (activeElement?.getAttribute('data-element-type') === 'button') {
          // remove previous conditional comment node if exists
          removePreviousConditionalCommentNode(activeElement);

          const buttonBackground = activeElement?.style.backgroundImage || '';
          const msoHideStyle = 'mso-hide:all;';
          let includeMsoHideStyle = false;

          // if the background image is set, prepend the conditional comment code
          if (buttonBackground && buttonBackground !== 'none' && buttonBackground.includes('url(')) {
            // create the VML for Outlook
            const vml = createButtonConditionalCommentVML(activeElement);

            if (vml) {
              // prepend the VML comment to the button element
              prependConditionalComment(activeElement, vml);

              includeMsoHideStyle = true;
            }
          }

          if (includeMsoHideStyle) {
            // set the mso-hide style to the button element
            activeElement.setAttribute(
              'style',
              getUniqueStyles(`${terminateStylesString(activeElement.getAttribute('style'))} ${msoHideStyle}`),
            );
          } else {
            // remove the mso-hide style from the button element
            activeElement.setAttribute(
              'style',
              getUniqueStyles(activeElement.getAttribute('style')?.replace(/mso-hide:all;/g, '') || ''),
            );
          }
        }

        // remove the anchor tag if the href is empty and its not a button
        if ((!href || href.trim() === '') && activeElement?.getAttribute('data-element-type') !== 'button') {
          const parent = activeElement?.parentElement;
          if (!parent) {
            return;
          }

          while (activeElement.firstChild) {
            parent.insertBefore(activeElement.firstChild, activeElement);
          }
          // Remove the anchor itself
          parent.removeChild(activeElement);
        }
      }
      // if tagname is span or img, look for parent A
      else if (tagName === 'span' || tagName === 'img') {
        const parentElement = activeElement?.parentElement;

        // if there is an href value, update or create the A element
        if (href || href.trim() !== '') {
          if (parentElement?.tagName.toLowerCase() === 'a') {
            parentElement?.setAttribute('href', href);
            parentElement?.setAttribute('temphref', href);
          }
          // otherwise create the A element
          else {
            const linkElem = document.createElement('a');
            linkElem.setAttribute('href', href);
            linkElem.setAttribute('temphref', href);

            // get the active elements calculated color style
            const color = window.getComputedStyle(activeElement).color;
            linkElem.style.color = color;

            activeElement?.replaceWith(linkElem);
            linkElem.appendChild(activeElement);
            return;
          }
        }
        // else the href is empty, remove the A element
        else {
          if (parentElement && parentElement.tagName.toLowerCase() === 'a') {
            const grandParent = parentElement.parentElement;
            if (!grandParent) {
              return;
            }

            while (parentElement.firstChild) {
              grandParent.insertBefore(parentElement.firstChild, parentElement);
            }
            // Remove the anchor itself
            grandParent.removeChild(parentElement);
          }
        }
      }
      // if tagname is div, h1-h6, p, look for first child A
      else if (['div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p'].includes(tagName)) {
        const firstChild = activeElement?.firstElementChild;

        // if there is an href value, update or create the A element
        if (href || href.trim() !== '') {
          if (firstChild?.tagName.toLowerCase() === 'a') {
            firstChild.setAttribute('href', href);
            firstChild.setAttribute('temphref', href);
          }
          // otherwise create the child A element
          else {
            const linkElem = document.createElement('a');
            linkElem.setAttribute('href', href);
            linkElem.setAttribute('temphref', href);

            // get the active elements calculated color style
            const color = window.getComputedStyle(activeElement).color;
            linkElem.style.color = color;

            // get the active elements children and append them to the link element
            const childNodes = Array.from(activeElement.childNodes);
            childNodes.forEach((childNode) => {
              linkElem.appendChild(childNode);
            });

            // replace the active element children with the new link element
            activeElement?.replaceChildren(linkElem);
          }
        }
        // else the href is empty, remove the A element
        else {
          if (firstChild && firstChild.tagName.toLowerCase() === 'a') {
            while (firstChild.firstChild) {
              activeElement.insertBefore(firstChild.firstChild, firstChild);
            }
            // Remove the anchor itself
            activeElement.removeChild(firstChild);
          }
        }
      }
    },
    [chosenModuleElemId],
  );

  const handleAltTextChange = useCallback(
    (altText: string) => {
      const { contentDocument } = document.getElementById('module-modal') as HTMLElementWithDocument;
      const curElem = contentDocument.getElementById(chosenModuleElemId) as HTMLElementWithOriginalInnerHTML;

      if (curElem.tagName.toLowerCase() === 'img') {
        if (!altText || altText.trim() === '') {
          curElem.removeAttribute('alt');
        } else {
          curElem.setAttribute('alt', altText);
        }
      }
    },
    [chosenModuleElemId],
  );

  const handleDVStyleChange = useCallback(
    (state: { width: number; height: number }) => {
      const iframe = document.getElementById('module-modal') as HTMLElementWithDocument;
      const imageElem = iframe.contentDocument.getElementById(chosenModuleElemId) as HTMLElementWithOriginalInnerHTML;

      if (imageElem) {
        imageElem.setAttribute('width', state?.width.toString() || '');
        imageElem.setAttribute('height', state?.height.toString() || '');
      }

      resizeModalIframe(iframe as HTMLIFrameElement, false);
      positionSelectedModuleIframeOverlay(selectedActiveModule);

      // adjust the position of the active overlay
      const activeOverlay = iframe.contentDocument.getElementById(MODULE_INTERACTION_ACTIVE_ID);
      if (activeOverlay && imageElem) {
        positionIframeOverlay(activeOverlay, imageElem);
      }
    },
    [chosenModuleElemId, selectedActiveModule],
  );

  // Handle changes to the element title text and aria-label attributes
  const handleTitleChange = useCallback(
    (title: string) => {
      const { contentDocument } = document.getElementById('module-modal') as HTMLElementWithDocument;
      const changingElem = contentDocument.getElementById(chosenModuleElemId) as HTMLElementWithOriginalInnerHTML;

      // logic for accessing the link URL based on the tag name
      const tagName = changingElem?.tagName.toLowerCase() ?? '';
      let targetAnchor: HTMLElement | null = null;

      // if tagname is A
      if (tagName === 'a') {
        targetAnchor = changingElem;

        changingElem.setAttribute('title', title);
        changingElem.setAttribute('aria-label', title);

        // if tagname is A and data-element-type is button
        if (changingElem?.getAttribute('data-element-type') === 'button') {
          // remove previous conditional comment node if exists
          removePreviousConditionalCommentNode(changingElem);

          const buttonBackground = changingElem?.style.backgroundImage || '';
          const msoHideStyle = 'mso-hide:all;';
          let includeMsoHideStyle = false;

          // if the background image is set, prepend the conditional comment code
          if (buttonBackground && buttonBackground !== 'none' && buttonBackground.includes('url(')) {
            // create the VML for Outlook
            const vml = createButtonConditionalCommentVML(changingElem);

            if (vml) {
              // prepend the VML comment to the button element
              prependConditionalComment(changingElem, vml);

              includeMsoHideStyle = true;
            }
          }

          if (includeMsoHideStyle) {
            // set the mso-hide style to the button element
            changingElem.setAttribute(
              'style',
              getUniqueStyles(`${terminateStylesString(changingElem.getAttribute('style'))} ${msoHideStyle}`),
            );
          } else {
            // remove the mso-hide style from the button element
            changingElem.setAttribute(
              'style',
              getUniqueStyles(changingElem.getAttribute('style')?.replace(/mso-hide:all;/g, '') || ''),
            );
          }
        }
      }
      // If tagname is span or img, look for parent A
      else if (tagName === 'span' || tagName === 'img') {
        const parentA = changingElem?.parentElement as HTMLElement | null;
        if (parentA?.tagName.toLowerCase() === 'a') {
          targetAnchor = parentA;
        }
      }
      // if tagname is h1-h6, p, look for first child A
      else if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p'].includes(tagName)) {
        const firstChild = changingElem?.firstElementChild as HTMLElement | null;

        if (firstChild?.tagName.toLowerCase() === 'a') {
          targetAnchor = firstChild;
        }
      }

      if (targetAnchor) {
        // set the title and aria-label attributes for the changing <A> element
        if (!title || title.trim() === '') {
          targetAnchor.removeAttribute('title');
          targetAnchor.removeAttribute('aria-label');
        } else {
          targetAnchor.setAttribute('title', title);
          targetAnchor.setAttribute('aria-label', title);
        }
      }
    },
    [chosenModuleElemId],
  );

  const handleButtonBGImageChange = useCallback(
    (state: ButtonBackgroundImageState) => {
      const { contentDocument } = document.getElementById('module-modal') as HTMLElementWithDocument;
      const buttonElem = contentDocument.getElementById(chosenModuleElemId) as HTMLElementWithOriginalInnerHTML;

      if (!buttonElem) {
        return;
      }

      const buttonElemStyles = buttonElem.getAttribute('style');
      const buttonOffsetHeight = buttonElem.offsetHeight;
      const buttonOffsetWidth = buttonElem.offsetWidth;

      // set the background styles for the button element
      buttonElem.setAttribute(
        'style',
        getUniqueStyles(
          `${terminateStylesString(buttonElemStyles)} ${getBackgroundImageStyles(
            state,
            buttonOffsetWidth,
            buttonOffsetHeight,
          )}`,
        ),
      );

      // remove previous conditional comment node if exists
      removePreviousConditionalCommentNode(buttonElem);

      // create the VML for Outlook
      const vml = createButtonConditionalCommentVML(buttonElem);
      let msoHideStyle = '';

      if (vml) {
        // prepend the VML comment to the button element
        prependConditionalComment(buttonElem, vml);
        msoHideStyle = 'mso-hide:all;';
      }

      // set the mso-hide style to the button element
      buttonElem.setAttribute(
        'style',
        getUniqueStyles(`${terminateStylesString(buttonElem.getAttribute('style'))} ${msoHideStyle}`),
      );
    },
    [chosenModuleElemId],
  );

  const handleDivStyleChange = useCallback(
    (state: DivContainerState) => {
      const iframe = document.getElementById('module-modal') as HTMLElementWithDocument;
      const divElem = iframe.contentDocument.getElementById(chosenModuleElemId) as HTMLElementWithOriginalInnerHTML;
      const divElemStyles = divElem?.getAttribute('style');
      const divOffsetWidth = divElem?.offsetWidth;
      const divOffsetHeight = divElem?.offsetHeight;

      divElem?.setAttribute(
        'style',
        getUniqueStyles(
          `${terminateStylesString(divElemStyles)} ${getBackgroundImageStyles(state, divOffsetWidth, divOffsetHeight)}`,
        ),
      );

      resizeModalIframe(iframe as HTMLIFrameElement, false);
      positionSelectedModuleIframeOverlay(selectedActiveModule);

      // adjust the position of the active overlay
      const activeOverlay = iframe.contentDocument.getElementById(MODULE_INTERACTION_ACTIVE_ID);
      if (activeOverlay && divElem) {
        positionIframeOverlay(activeOverlay, divElem);
      }
    },
    [chosenModuleElemId, selectedActiveModule],
  );

  const handleChooseImage = useCallback(
    (image: any) => {
      const { contentDocument } = document.getElementById('module-modal') as HTMLElementWithDocument;
      const changingElem = contentDocument.getElementById(chosenModuleElemId) as HTMLElementWithOriginalInnerHTML;

      // only attempt to change DV or Image if a module element has been selected and found in DOM
      if (changingElem) {
        // only allow changing image for img, div, and a tags. For div and a tags that are buttons,
        // the image will be set as a background image
        const validTags = ['img', 'div', 'a'];
        if (!validTags.includes(changingElem.tagName.toLowerCase())) {
          return;
        }

        if (image.thumbnailPath) {
          // if the chosen element is not an img tag, do not update with data visualization
          if (changingElem.tagName.toLowerCase() !== 'img') {
            return;
          }

          const payload = {
            chosenModuleElemId: chosenModuleElemId,
            height: image.height,
            imageSrc: image.thumbnailPath,
            name: image.name,
            width: image.width,
          };

          setChosenDV(image);
          setChosenImage(payload as any);
          setRightPanelElem(RightPanelElems.WYSIWYG_DATA_VISUAL);

          handleImageStyleChange(payload);

          // add data visualization attributes
          changingElem.setAttribute('data-visualization-station', 'true');
          changingElem.setAttribute('dataVisualId', image.id);
          changingElem.setAttribute('dataVisualEmbdedCode', image.embedCode);
          changingElem.setAttribute('isMaxScaleEnabled', image.isMaxScaleEnabled);
        } else {
          setChosenImage(image);

          switch (changingElem.tagName.toLowerCase()) {
            case 'img':
              handleImageStyleChange(image);
              setRightPanelElem(RightPanelElems.WYSIWYG_IMAGE);
              break;
            case 'div':
              handleDivStyleChange(image);
              // setRightPanelElem(RightPanelElems.WYSIWYG_DIV);
              break;
            case 'a': // only when element-type=button
              if (changingElem.getAttribute('data-element-type') === 'button') {
                handleButtonBGImageChange(image);
                // setRightPanelElem(RightPanelElems.BUTTON_SETTINGS);
              }
              break;
            default:
              return;
          }

          // remove data visualization attributes
          changingElem.removeAttribute('data-visualization-station');
          changingElem.removeAttribute('dataVisualId');
          changingElem.removeAttribute('dataVisualEmbdedCode');
          changingElem.removeAttribute('isMaxScaleEnabled');
          setModuleDataVisualizationStation(null);
        }
        setIsModuleChanged(true);
      }
    },
    [
      chosenModuleElemId,
      // rightPanelElem,
      handleButtonBGImageChange,
      handleDivStyleChange,
      handleImageStyleChange,
      setChosenImage,
      setChosenDV,
      setModuleDataVisualizationStation,
    ],
  );

  const onEditorChange = (value: string) => {
    setIsModuleChanged(true);
    setModuleHtml(value);
    setIsEditorChanged(true);
  };

  const handleTreatmentChange = useCallback<TreatmentCardProps['setChosenTreatmentId']>(
    (nextTreatmentId) => {
      if (nextTreatmentId !== chosenModuleId) {
        setChosenModuleId(nextTreatmentId);
      }
    },
    [setChosenModuleId, chosenModuleId],
  );

  const handleDimensionsChange = useCallback(
    ([width, height]: [number, number]) => {
      const payload = {
        height,
        width,
      };

      handleDVStyleChange(payload);
    },
    [handleDVStyleChange],
  );

  const selectTreatment = useCallback(
    (nextTreatment: ExperienceItemMindset) => {
      if (!nextTreatment) {
        // Handle the case where nextTreatment is undefined or null
        setChosenModuleInnerHtml(EMPTY_MODULE_NOTE);
        return;
      }

      handleTreatmentChange(nextTreatment.id);

      setDefaultChosenTreats((prevState: any) => {
        const chosenTreatment = {
          id: nextTreatment.id,
          experienceId: chosenExperienceId,
        };

        return [...prevState.filter((item: any) => item.experienceId !== chosenExperienceId), chosenTreatment];
      });

      setChosenModuleInnerHtml(nextTreatment.moduleHtml || EMPTY_MODULE_NOTE);
    },
    [chosenExperienceId, setChosenModuleInnerHtml, handleTreatmentChange, setDefaultChosenTreats],
  );

  // set topBarCtx.contentModuleName to latest module name of chosenTreatment if they are not equal
  // and the right panel is not Deployment Settings or Treatments
  useEffect(() => {
    if (
      ![RightPanelElems.DEPLOYMENT_SETTINGS, RightPanelElems.TREATMENTS].includes(rightPanelElem) &&
      topBarCtx.contentModuleName !== topBarCtx.chosenTreatment?.moduleTemplateName
    ) {
      const contentModuleName = topBarCtx.chosenTreatment?.moduleTemplateName;
      setTopBarCtx((prev) => ({
        ...prev,
        contentModuleName,
        //contentModuleName: topBarCtx.chosenTreatment?.moduleTemplateName,
      }));
    }
  }, [rightPanelElem, setTopBarCtx, topBarCtx.chosenTreatment?.moduleTemplateName, topBarCtx.contentModuleName]);

  const getRightPanelElem = useCallback(
    () => {
      /*
      if (
        ![RightPanelElems.DEPLOYMENT_SETTINGS, RightPanelElems.TREATMENTS].includes(rightPanelElem) &&
        topBarCtx.contentModuleName !== topBarCtx.chosenTreatment?.moduleTemplateName
      ) {
        const contentModuleName = topBarCtx.chosenTreatment?.moduleTemplateName;
        setTopBarCtx((prev) => ({
          ...prev,
          contentModuleName,
          //contentModuleName: topBarCtx.chosenTreatment?.moduleTemplateName,
        }));
      }
        */

      if (editMode === 'html') {
        return (
          <HTMLEditor
            moduleHtml={moduleHtml}
            moduleName={topBarCtx.contentModuleName ?? ''}
            onEditorChange={onEditorChange}
          />
        );
      }

      switch (rightPanelElem) {
        case RightPanelElems.DEPLOYMENT_SETTINGS:
          // if (topBarCtx.contentModuleName !== '') {
          //   setTopBarCtx((prev) => ({
          //     ...prev,
          //     contentModuleName: '',
          //   }));
          // }

          return <DeploymentSettings />;
        case RightPanelElems.BUTTON_SETTINGS:
          return (
            <Wysiwyg.ButtonSettings
              chosenImage={chosenImage}
              isModuleChanged={isModuleChanged}
              setIsModuleChanged={setIsModuleChanged}
              initialValues={buttonSettingsInitialValues}
              containerType={wysiwygContainerType}
              onChange={handleButtonStyleChange}
              onLinkChange={handleLinkChange}
              onTitleChange={handleTitleChange}
              onModuleClose={onModuleClose}
              onModuleSave={onModuleSave}
            />
          );
        case RightPanelElems.TREATMENTS:
          return (
            <Treatments
              setTreatmentPanelType={setTreatmentPanelType}
              templateId={templateId}
              setRightPanelElem={setRightPanelElem}
              setDeletedExperience={setDeletedExperience}
            />
          );
        case RightPanelElems.WYSIWYG_UNIVERSAL:
          return (
            <Wysiwyg.UniversalContainer
              isModuleChanged={isModuleChanged}
              setIsModuleChanged={setIsModuleChanged}
              initialValues={universalInitialValues}
              containerType={wysiwygContainerType}
              onChange={handleUniversalStyleChange}
              onLinkChange={handleLinkChange}
              onTitleChange={handleTitleChange}
              onModuleClose={onModuleClose}
              onModuleSave={onModuleSave}
            />
          );
        case RightPanelElems.WYSIWYG_IMAGE:
          return (
            <Wysiwyg.ImageContainer
              isModuleChanged={isModuleChanged}
              setIsModuleChanged={setIsModuleChanged}
              setChosenLibrary={setChosenLibrary}
              chosenImage={chosenImage}
              onChange={handleImageStyleChange}
              onLinkChange={handleLinkChange}
              onTitleChange={handleTitleChange}
              onAltTextChange={handleAltTextChange}
              onModuleClose={onModuleClose}
              onModuleSave={onModuleSave}
            />
          );
        case RightPanelElems.WYSIWYG_DATA_VISUAL:
          return (
            <Wysiwyg.DVPanel
              chosenDV={chosenDV!}
              isModuleChanged={isModuleChanged}
              setIsModuleChanged={setIsModuleChanged}
              setChosenLibrary={setChosenLibrary}
              onImageChange={handleImageStyleChange}
              onDimensionsChange={handleDimensionsChange}
              onModuleClose={onModuleClose}
              onModuleSave={onModuleSave}
            />
          );
        case RightPanelElems.WYSIWYG_DIV:
          return (
            <Wysiwyg.DivContainer
              isModuleChanged={isModuleChanged}
              setIsModuleChanged={setIsModuleChanged}
              initialValues={divInitialValues}
              // initialValues={getDivInitialValues(transformStylesStringToObject(chosenElemStyles))}
              chosenImage={chosenImage}
              onChange={handleDivStyleChange}
              setChosenLibrary={setChosenLibrary}
              onModuleClose={onModuleClose}
              onModuleSave={onModuleSave}
            />
          );
        case RightPanelElems.MODULE_SETTINGS:
          return (
            <ModuleSettings
              onEditorChange={onEditorChange}
              module={{ id: chosenModuleId, moduleHtml, name: topBarCtx.contentModuleName }}
              isModuleChanged={isModuleChanged}
              onModuleClose={onModuleClose}
              onModuleSave={onModuleSave}
            />
          );
      }
    },
    [
      editMode,
      rightPanelElem,
      moduleHtml,
      topBarCtx.contentModuleName,
      chosenImage,
      isModuleChanged,
      buttonSettingsInitialValues,
      wysiwygContainerType,
      handleButtonStyleChange,
      handleLinkChange,
      handleTitleChange,
      onModuleClose,
      onModuleSave,
      templateId,
      universalInitialValues,
      handleUniversalStyleChange,
      handleImageStyleChange,
      handleAltTextChange,
      chosenDV,
      handleDimensionsChange,
      divInitialValues,
      handleDivStyleChange,
      chosenModuleId,
    ],
    /* [
    chosenElemStyles,
    chosenImage,
    chosenModuleElemId,
    handleDivStyleChange,
    handleImageStyleChange,
    handleUniversalStyleChange,
    isModuleChanged,
    rightPanelElem,
    templateId,
    editMode,
    wysiwygContainerType,
    topBarCtx.contentModuleName,
    topBarCtx.chosenTreatment,
  ] */
  );

  const handleTreatmentPanelClose = () => {
    setShowAddTreatmentPanel(false);
    setIsTargetContentShown(true);
  };

  const handleModuleHtmlChange = useCallback(
    (curModuleHtml: string, clearModuleHtml: string) => {
      if (
        [RightPanelElems.MODULE_SETTINGS, RightPanelElems.TREATMENTS].includes(rightPanelElem) &&
        moduleHtml !== clearModuleHtml &&
        clearModuleHtml !== '<body></body>' &&
        moduleHtml !== curModuleHtml
      ) {
        setModuleHtml(clearModuleHtml);
      }

      // get selected module element from chosenModuleElemId
      const moduleElem = document?.getElementById('module-modal') as HTMLElementWithDocument;
      const selectedElem = moduleElem?.contentDocument.getElementById(chosenModuleElemId);

      // if the selected module and the current module is a button with a background, update the conditional comment
      if (selectedElem && selectedElem.getAttribute('data-element-type') === 'button') {
        removePreviousConditionalCommentNode(selectedElem);

        const msoHideStyle = 'mso-hide:all;';
        let includeMsoHideStyle = false;

        const buttonBackground = selectedElem?.style.backgroundImage || '';

        if (buttonBackground && buttonBackground !== 'none' && buttonBackground.includes('url(')) {
          // create the VML for Outlook
          const vml = createButtonConditionalCommentVML(selectedElem);

          if (vml) {
            // prepend the VML comment to the button element
            prependConditionalComment(selectedElem, vml);

            includeMsoHideStyle = true;
          }
        }

        if (includeMsoHideStyle) {
          // set the mso-hide style to the button element
          selectedElem.setAttribute(
            'style',
            getUniqueStyles(`${terminateStylesString(selectedElem.getAttribute('style'))} ${msoHideStyle}`),
          );
        } else {
          // remove the mso-hide style from the button element
          selectedElem.setAttribute(
            'style',
            getUniqueStyles(selectedElem.getAttribute('style')?.replace(/mso-hide:all;/g, '') || ''),
          );
        }
      }
    },
    [chosenModuleElemId, moduleHtml, rightPanelElem],
  );

  useEffect(() => {
    const moduleElem = document?.getElementById('module-modal') as HTMLElementWithDocument;
    const imageElem = moduleElem?.contentDocument.getElementById(
      chosenModuleElemId,
    ) as HTMLElementWithOriginalInnerHTML;

    if (imageElem && chosenModuleElemId.indexOf('img') !== -1) {
      setTimeout(() => {
        const imageSrc = imageElem.getAttribute('src');
        const width = Math.round(imageElem.getBoundingClientRect().width);
        const height = Math.round(imageElem.getBoundingClientRect().height);
        const dataVisualId = Number(imageElem.getAttribute('dataVisualId'));
        const dataVisualEmbedCode = imageElem.getAttribute('dataVisualEmbdedCode');
        const isMaxScaleEnabled = imageElem.getAttribute('isMaxScaleEnabled') === 'false' ? false : true;
        const linkParent = imageElem.parentElement?.tagName === 'A' ? imageElem.parentElement : null;
        const linkValue = linkParent?.getAttribute('href') ?? linkParent?.getAttribute('temphref') ?? '';
        const linkTitleValue = linkParent?.getAttribute('title') ?? '';
        const altTextValue = imageElem.getAttribute('alt') ?? '';

        if (chosenDV?.id !== dataVisualId) {
          setChosenDV({
            id: dataVisualId ?? 0,
            embedCode: dataVisualEmbedCode,
            isMaxScaleEnabled,
            width,
            imageSrc: '',
            height,
          } as any);
        }

        setChosenImage({
          id: null,
          chosenModuleElemId,
          imageSrc,
          width: width ? +width : DEFAULT_IMAGE_WRAPPER_WIDTH,
          height: height ? +height : DEFAULT_IMAGE_WRAPPER_HEIGHT,
          name: null,
          link: linkValue,
          title: linkTitleValue,
          altText: altTextValue,
        });
      }, 150);
    } else if (imageElem && chosenModuleElemId.indexOf('div') !== -1) {
      const styleAttr = imageElem.getAttribute('style') ?? '';
      const bgImageMatch = styleAttr.match(/background-image:\s*([^;]+)/);
      const imageSrc = bgImageMatch ? bgImageMatch[1].trim() : 'none';
      const width = Math.round(imageElem.getBoundingClientRect().width);
      const height = Math.round(imageElem.getBoundingClientRect().height);

      setChosenImage({
        id: null,
        chosenModuleElemId,
        imageSrc,
        width: width ? +width : DEFAULT_IMAGE_WRAPPER_WIDTH,
        height: height ? +height : DEFAULT_IMAGE_WRAPPER_HEIGHT,
        name: null,
        link: null,
        title: null,
        altText: null,
      });
    } else {
      setChosenImage(DEFAULT_CHOSEN_IMAGE);
    }
  }, [chosenModuleElemId, chosenDV]);

  useEffect(() => {
    setTopBarCtx((prev) => ({
      ...prev,
      variant: 'section',
      sectionName: textTemplate(content.entityManager, { entity: content.deployment }),
    }));

    return () => {
      setTopBarCtx(() => TOP_BAR_CTX_VALUE);
    };
  }, [setTopBarCtx]);

  return (
    <Box className={styles.templateView}>
      <DragDropContext onDragEnd={handleDragEnd}>
        <TreatmentBuilderContext.Provider value={contextValues}>
          {!isModuleEditorOpen && (
            <SubjectLinePreheader
              resetComponent={resetSubjectLinePreheader}
              setResetComponent={setResetSubjectLinePreheader}
            />
          )}
          <Section className={styles.leftPanel} range={2} gap={0}>
            {!topBarCtx.isTemplateListVisible ? (
              <>
                {!showLibraryPanel ? (
                  <ModuleLibrary
                    ref={sidebarRef}
                    businessUnitId={businessUnitId}
                    onModuleDrag={handleModuleDrag}
                    onModulesDrag={handleDragExperience}
                    usedExperiences={usedExperiences}
                  />
                ) : (
                  <LibrariesPanelWrapper onChooseLibrary={handleChooseLibrary}>
                    {getLibraryPanelComponent(chosenLibrary, false, handleChooseImage, undefined, setChosenImage)}
                  </LibrariesPanelWrapper>
                )}
              </>
            ) : (
              <>
                <TemplateList
                  items={templates}
                  selected={selectedTemplate}
                  showSelectTemplate={showSelectTemplate}
                  onSelect={onTemplateSelect}
                  onUpload={onTemplateUpload}
                />
              </>
            )}
          </Section>
          <Section className={styles.centralPanel} range={6} gap={0}>
            {template && (
              <SampleTemplate
                templateId={templateId}
                sidebarRef={sidebarRef}
                deletedExperience={deletedExperience}
                changedModuleHtml={moduleHtml}
                isEditorChanged={isEditorChanged}
                rightPanelElem={rightPanelElem}
                isModuleEditorOpen={isModuleEditorOpen}
                setIsEditorChanged={setIsEditorChanged}
                setChangedModuleHtml={setModuleHtml}
                setDeletedExperience={setDeletedExperience}
                setUsedExperiences={setUsedExperiences}
                setShowLibraryPanel={setShowLibraryPanel}
                setRightPanelElem={setRightPanelElem}
                onModuleHtmlChange={handleModuleHtmlChange}
                setIsModuleEditorOpen={setIsModuleEditorOpen}
                setActiveModule={setSelectedActiveModule}
              />
            )}
            {showSelectTemplate && (
              <>
                <Box className={styles.moduleHeaderPlaceholder}></Box>
                <Box className={styles.deploymentsInfo}>
                  <EmptyPlaceholder
                    title={content.selectATemplate}
                    icon={<Icon.Add fill={variables.color.primary.darkestGray} />}
                  />
                </Box>
              </>
            )}
          </Section>
          {showAddTreatmentPanel && (
            <Box className={styles.overlayBackground}>
              <Box
                className={styles.overlayContainer}
                // range={6} gap={0}
              >
                <ContentTargetingOverlay
                  treatmentPanelType={treatmentPanelType}
                  selectTreatment={selectTreatment}
                  onClose={handleTreatmentPanelClose}
                />
              </Box>
            </Box>
          )}
          <Section className={styles.rightPanel} range={4} gap={0}>
            <RightPanelLayout>{getRightPanelElem()}</RightPanelLayout>
          </Section>
        </TreatmentBuilderContext.Provider>
      </DragDropContext>
    </Box>
  );
};
